import React, { useContext } from 'react';
import { Dimensions, Modal, StyleSheet, View } from 'react-native';
import DyteClient from '@dytesdk/web-core';
import { useDyteSelector } from '@dytesdk/react-native-core';
import { defaultConfig } from '../lib/default-ui-config';
import { UIConfig } from '../types/ui-config';
import { Peer } from '../types/dyte-client';
import { defaultIconPack, IconPack } from '../lib/icons';
import { DyteI18n, useLanguage } from '../lib/lang';
import { States } from '../types/props';
import DyteText from './DyteText';
import DyteLiveStreamPlayer from './DyteLiveStreamPlayer';
import DyteHighlightedGrid from './DyteHighligtedGrid';
import DyteSpinner from './DyteSpinner';
import { DyteUIContext } from '../lib/context-provider/ui-context';

type Props = {
  height?: number;
};

export default function DyteSpotlightGrid({
  aspectRatio = '3:4',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  config = defaultConfig,
  gap = 4,
  iconPack = defaultIconPack,
  meeting,
  participants,
  pinnedParticipants,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  size = 'sm',
  states,
  t,
}: {
  aspectRatio?: string;
  config?: UIConfig;
  gap?: number;
  iconPack?: IconPack;
  meeting: DyteClient;
  participants: Peer[];
  pinnedParticipants: Peer[];
  size?: 'lg' | 'md' | 'sm' | 'xl';
  states?: States;
  t?: DyteI18n;
}) {
  const defaultLang = useLanguage();
  t = t ?? defaultLang;
  iconPack = iconPack ?? defaultIconPack;
  const livestream = useDyteSelector((m) => m.livestream);
  const livestreamStatus = livestream?.state ?? null;
  const stage = useDyteSelector((m) => m.stage);
  const stageStatus = stage.status;
  const { storeStates, setStates } = useContext(DyteUIContext);
  const { colors } = storeStates.designSystem;
  states = states ?? storeStates;
  const visible = storeStates.activeFullscreen;
  const closeFullScreen = () => {
    return setStates({ ...states, activeFullscreen: false });
  };
  const { width, height } = Dimensions.get('screen');
  const styles = StyleSheet.create({
    gridContainer: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.background[900],
    },
    gridStyle: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-evenly',
      alignItems: 'center',
      overflow: 'scroll',
    },
    fullScreenContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: 8,
      backgroundColor: colors.background[900],
    },
    fullscreen: {
      width: height * 0.95,
      height: width * 0.95,
      justifyContent: 'center',
      alignItems: 'center',
      transform: [{ rotate: '90deg' }],
    },
  });
  if (
    meeting.meta.viewType === 'LIVESTREAM' &&
    stageStatus !== 'ON_STAGE' &&
    livestreamStatus !== 'LIVESTREAMING'
  )
    return (
      // eslint-disable-next-line react-native/no-inline-styles
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
        <DyteText>{t('livestream.idle')}</DyteText>
      </View>
    );
  if (
    meeting.meta.viewType === 'LIVESTREAM' &&
    stageStatus !== 'ON_STAGE' &&
    livestreamStatus === 'STARTING'
  )
    return (
      <View
        // eslint-disable-next-line react-native/no-inline-styles
        style={{
          flex: 1,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* eslint-disable-next-line react-native/no-inline-styles */}
        <DyteText style={{ marginRight: 8 }}>
          {t('livestream.starting')}
        </DyteText>
        <DyteSpinner />
      </View>
    );
  if (
    meeting.meta.viewType === 'LIVESTREAM' &&
    stageStatus !== 'ON_STAGE' &&
    livestreamStatus === 'LIVESTREAMING'
  )
    return (
      // eslint-disable-next-line react-native/no-inline-styles
      <View style={{ flex: 1 }}>
        {visible && (
          <Modal transparent visible={visible} onRequestClose={closeFullScreen}>
            <View style={styles.fullScreenContainer}>
              <View style={styles.fullscreen}>
                <DyteLiveStreamPlayer />
              </View>
            </View>
          </Modal>
        )}
        <DyteLiveStreamPlayer />
      </View>
    );
  if (
    meeting.meta.viewType === 'LIVESTREAM' &&
    stageStatus !== 'ON_STAGE' &&
    livestreamStatus === 'STOPPING'
  )
    return (
      <View
        // eslint-disable-next-line react-native/no-inline-styles
        style={{
          flex: 1,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* eslint-disable-next-line react-native/no-inline-styles */}
        <DyteText style={{ marginRight: 8 }}>
          {t('livestream.stopping')}
        </DyteText>
        <DyteSpinner />
      </View>
    );
  return (
    <DyteHighlightedGrid
      meeting={meeting}
      participants={participants}
      pinnedParticipants={pinnedParticipants}
      screenshareParticipants={[]}
      plugins={[]}
      aspectRatio={aspectRatio}
      gap={gap}
      iconPack={iconPack}
      t={t}
    />
  );
}

DyteSpotlightGrid.defaultProps = {
  height: 0,
};
